/**
 * Settings Tab Component - Supplier-specific configuration and settings
 * Includes OTIF window settings, company information editing, supplier config,
 * and customer relationship management.
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Loader2, Settings, Clock, Info, Star,
  ShieldCheck, Layers, UserCog, BarChart3, PackageCheck,
} from 'lucide-react';
import { coreAPI, purchasingAPI, SupplierProfile, SupplierCustomerRelationship, CommodityGroup, CommodityType, Site, UserProfile as APIUserProfile } from '../../lib/api';
import { OtifSettings } from './types';
import { toast } from 'sonner';
import { CustomerRelationshipsSection } from './CustomerRelationshipsSection';
import { useQualityMeasureLabel } from '../../lib/qualityLabels';

interface SettingsTabProps {
  supplier: SupplierProfile;
  otifSettings: OtifSettings | null;
  otifLoading: boolean;
  otifSaving: boolean;
  isHelixAdmin: boolean;
  isCustomer: boolean;
  onOtifSettingsUpdate: (settings: Partial<OtifSettings>) => Promise<void>;
  setOtifSettings: React.Dispatch<React.SetStateAction<OtifSettings | null>>;
  onUpdateProfile: (data: Partial<SupplierProfile>) => Promise<void>;
}

export function SettingsTab({
  supplier, otifSettings, otifLoading, otifSaving,
  isHelixAdmin, isCustomer, onOtifSettingsUpdate, setOtifSettings,
  onUpdateProfile,
}: SettingsTabProps) {
  const canEditSettings = isHelixAdmin || isCustomer;
  const qualityLabel = useQualityMeasureLabel();

  // Customer Relationships state
  const [relationships, setRelationships] = useState<SupplierCustomerRelationship[]>([]);
  const [relLoading, setRelLoading] = useState(false);
  const [expandedRelId, setExpandedRelId] = useState<number | null>(null);
  const [relSaving, setRelSaving] = useState<number | null>(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [availableSites, setAvailableSites] = useState<Site[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [assigning, setAssigning] = useState(false);

  // CR-4362: Commodity Settings state
  const [commodityGroups, setCommodityGroups] = useState<CommodityGroup[]>([]);
  const [commodityTypes, setCommodityTypes] = useState<CommodityType[]>([]);
  const [commoditySaving, setCommoditySaving] = useState(false);

  // CR-4363: Management Settings state
  const [supplierUsers, setSupplierUsers] = useState<APIUserProfile[]>([]);
  const [managementSaving, setManagementSaving] = useState(false);

  const fetchSupplierUsers = useCallback(async () => {
    try {
      const res = await coreAPI.getCompanyUsers(supplier.company);
      setSupplierUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err: unknown) {
      console.error('Error loading supplier users:', err);
    }
  }, [supplier.company]);

  const updateManagement = async (data: Partial<SupplierProfile>) => {
    setManagementSaving(true);
    try {
      await onUpdateProfile(data);
      toast.success('Management settings updated');
    } catch {
      toast.error('Failed to update management settings');
    } finally {
      setManagementSaving(false);
    }
  };

  const fetchCommodities = useCallback(async () => {
    try {
      const [gRes, tRes] = await Promise.all([
        purchasingAPI.getCommodityGroups({ page_size: 100 }),
        purchasingAPI.getCommodityTypes({ page_size: 500 }),
      ]);
      const gData = gRes.data;
      const tData = tRes.data;
      setCommodityGroups(Array.isArray(gData) ? gData : (gData as Record<string, unknown>)['results'] as CommodityGroup[]);
      setCommodityTypes(Array.isArray(tData) ? tData : (tData as Record<string, unknown>)['results'] as CommodityType[]);
    } catch (err: unknown) {
      console.error('Error loading commodities:', err);
    }
  }, []);

  const updateCommodity = async (data: Partial<SupplierProfile>) => {
    setCommoditySaving(true);
    try {
      await onUpdateProfile(data);
      toast.success('Commodity settings updated');
    } catch {
      toast.error('Failed to update commodity settings');
    } finally {
      setCommoditySaving(false);
    }
  };

  const fetchRelationships = useCallback(async () => {
    setRelLoading(true);
    try {
      const res = await coreAPI.getSupplierRelationships({ supplier: supplier.company, page_size: 100 });
      const data = res.data;
      setRelationships(Array.isArray(data) ? data : (data as Record<string, unknown>)['results'] as SupplierCustomerRelationship[]);
    } catch (err: unknown) {
      console.error('Error loading relationships:', err);
    } finally {
      setRelLoading(false);
    }
  }, [supplier.company]);

  useEffect(() => {
    fetchRelationships();
    fetchCommodities();
    fetchSupplierUsers();
  }, [fetchRelationships, fetchCommodities, fetchSupplierUsers]);

  const updateRelationship = async (id: number, data: Partial<SupplierCustomerRelationship>) => {
    setRelSaving(id);
    try {
      await coreAPI.updateSupplierRelationship(id, data);
      setRelationships((prev) => prev.map((r) => (r.id === id ? { ...r, ...data } : r)));
      toast.success('Relationship updated');
    } catch (err: unknown) {
      console.error('Error updating relationship:', err);
      toast.error('Failed to update relationship');
    } finally {
      setRelSaving(null);
    }
  };

  const openAssignDialog = async () => {
    try {
      const res = await coreAPI.getAccessibleSites();
      const sites: Site[] = Array.isArray(res.data) ? res.data : (res.data as Record<string, unknown>)['results'] as Site[];
      const existingSiteIds = new Set(relationships.map((r) => r.customer));
      setAvailableSites(sites.filter((s) => !existingSiteIds.has(s.id)));
      setSelectedSiteId(null);
      setAssignDialogOpen(true);
    } catch (err: unknown) {
      console.error('Error loading sites:', err);
      toast.error('Failed to load sites');
    }
  };

  const assignSite = async () => {
    if (!selectedSiteId) return;
    setAssigning(true);
    try {
      const res = await coreAPI.createSupplierRelationship({ supplier: supplier.company, customer: selectedSiteId });
      setRelationships((prev) => [...prev, res.data]);
      setAssignDialogOpen(false);
      toast.success('Customer assigned');
    } catch (err: unknown) {
      console.error('Error assigning site:', err);
      toast.error('Failed to assign customer');
    } finally {
      setAssigning(false);
    }
  };

  const removeRelationship = async (id: number) => {
    try {
      await coreAPI.deleteSupplierRelationship(id);
      setRelationships((prev) => prev.filter((r) => r.id !== id));
      setExpandedRelId(null);
      toast.success('Relationship removed');
    } catch (err: unknown) {
      console.error('Error removing relationship:', err);
      toast.error('Failed to remove relationship');
    }
  };


  return (
    <div className="space-y-6">
      {/* CR-4819: Company Information moved to Overview tab to remove duplication */}

      {/* Customer Relationships */}
      <CustomerRelationshipsSection
        relationships={relationships} relLoading={relLoading} expandedRelId={expandedRelId}
        setExpandedRelId={setExpandedRelId} relSaving={relSaving}
        canEditSettings={canEditSettings}
        assignDialogOpen={assignDialogOpen} setAssignDialogOpen={setAssignDialogOpen}
        availableSites={availableSites} selectedSiteId={selectedSiteId}
        setSelectedSiteId={setSelectedSiteId} assigning={assigning}
        setRelationships={setRelationships} updateRelationship={updateRelationship}
        removeRelationship={removeRelationship} openAssignDialog={openAssignDialog}
        assignSite={assignSite}
      />

      {/* Supplier Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5" />Supplier Configuration</CardTitle>
          <CardDescription>Configure supplier status and preferences</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="space-y-0.5">
                <label className="text-sm font-medium flex items-center gap-2"><Star className="h-4 w-4 text-blue-600" />Strategic Supplier</label>
                <p className="text-xs text-muted-foreground">Mark this supplier as a strategic vendor</p>
              </div>
              <div className="flex items-center gap-2">
                {supplier.is_preferred && (<Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">Strategic</Badge>)}
                <Switch checked={supplier.is_preferred} onCheckedChange={async (checked) => {
                  if (canEditSettings) {
                    try { await onUpdateProfile({ is_preferred: checked } as Partial<SupplierProfile>); toast.success(checked ? 'Supplier marked as strategic' : 'Strategic status removed'); }
                    catch { toast.error('Failed to update strategic status'); }
                  }
                }} disabled={!canEditSettings} />
              </div>
            </div>

            {/* Call-Off — one row per customer relationship */}
            {relationships.map((rel) => (
              <div key={`calloff-${rel.id}`} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <PackageCheck className="h-4 w-4 text-teal-600" />
                    Call-Off
                    {relationships.length > 1 && (
                      <span className="text-xs text-muted-foreground font-normal">— {rel.customer_name}</span>
                    )}
                  </label>
                  <p className="text-xs text-muted-foreground">Enable call-off and stock management for this supplier</p>
                </div>
                <div className="flex items-center gap-2">
                  {rel.call_off_enabled && (<Badge className="bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200">Enabled</Badge>)}
                  {relSaving === rel.id && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                  <Switch
                    checked={!!rel.call_off_enabled}
                    onCheckedChange={(checked) => { if (canEditSettings) updateRelationship(rel.id, { call_off_enabled: checked }); }}
                    disabled={!canEditSettings || relSaving === rel.id}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* OTIF Window Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Clock className="h-5 w-5" />OTIF Window Settings</CardTitle>
          <CardDescription>Configure On-Time In-Full delivery window tolerances for this supplier</CardDescription>
        </CardHeader>
        <CardContent>
          {otifLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : otifSettings ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <label className="text-sm font-medium">Use Custom OTIF Window</label>
                  <p className="text-xs text-muted-foreground">Override company defaults with supplier-specific OTIF tolerances</p>
                </div>
                <Switch checked={otifSettings.use_custom_otif_window} onCheckedChange={(checked) => { if (canEditSettings) { setOtifSettings({ ...otifSettings, use_custom_otif_window: checked }); onOtifSettingsUpdate({ use_custom_otif_window: checked }); } }} disabled={!canEditSettings || otifSaving} />
              </div>
              {otifSettings.use_custom_otif_window && (
                <div className="grid gap-4 md:grid-cols-2">
                  {[
                    { label: 'Days Early Allowed', field: 'otif_days_early_allowed' as const, hint: 'Deliveries this many days early are still considered on-time' },
                    { label: 'Days Late Allowed', field: 'otif_days_late_allowed' as const, hint: 'Deliveries this many days late are still considered on-time' },
                  ].map(({ label, field, hint }) => (
                    <div key={field} className="space-y-2">
                      <label className="text-sm font-medium">{label}</label>
                      <div className="flex items-center gap-2">
                        <input type="number" min="0" max="30" value={otifSettings[field]} onChange={(e) => { if (canEditSettings) { const value = parseInt(e.target.value) || 0; setOtifSettings({ ...otifSettings, [field]: value }); } }} onBlur={() => { if (canEditSettings) onOtifSettingsUpdate({ [field]: otifSettings[field] }); }} className="w-20 px-3 py-2 border rounded-md text-sm" disabled={!canEditSettings || otifSaving} />
                        <span className="text-sm text-muted-foreground">days</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{hint}</p>
                    </div>
                  ))}
                </div>
              )}
              {!otifSettings.use_custom_otif_window && (
                <div className="bg-muted/30 p-4 rounded-lg">
                  <div className="flex items-start gap-2"><Info className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" /><p className="text-sm text-muted-foreground">Using company default OTIF settings. Enable custom settings above to configure supplier-specific tolerances.</p></div>
                </div>
              )}
              {otifSaving && (<div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Saving settings...</div>)}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground"><p>Unable to load OTIF settings</p></div>
          )}
        </CardContent>
      </Card>

      {/* CR-4362: Commodity Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Layers className="h-5 w-5" />Commodity Settings</CardTitle>
          <CardDescription>Assign commodity group and type classification for this supplier</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Commodity Group</label>
              <select value={supplier.commodity_group ?? ''} onChange={(e) => { const val = e.target.value ? parseInt(e.target.value) : null; updateCommodity({ commodity_group: val, commodity_type: null } as Partial<SupplierProfile>); }} className="w-full px-3 py-2 border rounded-md text-sm bg-background" disabled={!canEditSettings || commoditySaving}>
                <option value="">None</option>
                {commodityGroups.map((g) => (<option key={g.id} value={g.id}>{g.name}</option>))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Commodity Type</label>
              <select value={supplier.commodity_type ?? ''} onChange={(e) => { const val = e.target.value ? parseInt(e.target.value) : null; updateCommodity({ commodity_type: val } as Partial<SupplierProfile>); }} className="w-full px-3 py-2 border rounded-md text-sm bg-background" disabled={!canEditSettings || commoditySaving || !supplier.commodity_group}>
                <option value="">None</option>
                {commodityTypes.filter((t) => t.commodity_group === supplier.commodity_group).map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
              </select>
            </div>
          </div>
          {commoditySaving && (<div className="flex items-center gap-2 text-sm text-muted-foreground mt-3"><Loader2 className="h-4 w-4 animate-spin" />Saving...</div>)}
        </CardContent>
      </Card>

      {/* CR-4363: Management Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><UserCog className="h-5 w-5" />Management Settings</CardTitle>
          <CardDescription>Assign commodity manager and supplier development manager for this supplier</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Commodity Manager</label>
              <select value={supplier.commodity_manager ?? ''} onChange={(e) => { const val = e.target.value ? parseInt(e.target.value) : null; updateManagement({ commodity_manager: val } as Partial<SupplierProfile>); }} className="w-full px-3 py-2 border rounded-md text-sm bg-background" disabled={!canEditSettings || managementSaving}>
                <option value="">Not assigned</option>
                {supplierUsers.map((u) => (<option key={u.user.id} value={u.user.id}>{u.user.full_name || u.user.email}</option>))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Supplier Development Manager</label>
              <select value={supplier.supplier_development_manager ?? ''} onChange={(e) => { const val = e.target.value ? parseInt(e.target.value) : null; updateManagement({ supplier_development_manager: val } as Partial<SupplierProfile>); }} className="w-full px-3 py-2 border rounded-md text-sm bg-background" disabled={!canEditSettings || managementSaving}>
                <option value="">Not assigned</option>
                {supplierUsers.map((u) => (<option key={u.user.id} value={u.user.id}>{u.user.full_name || u.user.email}</option>))}
              </select>
            </div>
          </div>
          {managementSaving && (<div className="flex items-center gap-2 text-sm text-muted-foreground mt-3"><Loader2 className="h-4 w-4 animate-spin" />Saving...</div>)}
        </CardContent>
      </Card>

      {/* CR-4365: KPI Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5" />KPI Settings</CardTitle>
          <CardDescription>Configure which KPI calculations include this supplier</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[
              { field: 'include_in_otif' as const, label: 'Include in OTIF', description: 'Include this supplier in On-Time In-Full calculations' },
              { field: 'include_in_ncr_ppm' as const, label: `Include in NCR/${qualityLabel}`, description: `Include this supplier in NCR and ${qualityLabel} calculations` },
              { field: 'include_in_otd' as const, label: 'Include in OTD', description: 'Include this supplier in On-Time Delivery calculations' },
            ].map(({ field, label, description }) => (
              <div key={field} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <label className="text-sm font-medium">{label}</label>
                  <p className="text-xs text-muted-foreground">{description}</p>
                </div>
                <Switch checked={supplier[field]} onCheckedChange={async (checked) => { if (canEditSettings) { try { await onUpdateProfile({ [field]: checked } as Partial<SupplierProfile>); toast.success(`${label} ${checked ? 'enabled' : 'disabled'}`); } catch { toast.error(`Failed to update ${label}`); } } }} disabled={!canEditSettings} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Audit Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5" />Audit Schedule</CardTitle>
          <CardDescription>Audit tracking and scheduling information</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider">Last Audit Date</label>
              <p className="font-medium">{supplier.last_audit_date ? new Date(supplier.last_audit_date).toLocaleDateString() : 'No audit recorded'}</p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider">Next Audit Date</label>
              <p className="font-medium">{supplier.next_audit_date ? new Date(supplier.next_audit_date).toLocaleDateString() : 'Not scheduled'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {!canEditSettings && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">You have view-only access to supplier settings. Contact your administrator for modifications.</p>
          </div>
        </div>
      )}

    </div>
  );
}
